import { useQuery } from "@tanstack/react-query";
import { api } from "@/lib/api-client";
import { useAuthStore } from "@/store/auth-store";
import type { PaginatedResponse } from "@/types/api";

export interface DaySummary {
  date: string;
  count: number;
  pax: number;
}

/** Per-day reservation counts for ``[dateFrom, dateTo]`` (inclusive,
 *  ISO ``YYYY-MM-DD``). One round-trip serves the entire week shown in
 *  the staff /reservas day picker — caching is keyed by the window so
 *  paging weeks ahead/back triggers exactly one extra GET each.
 *
 *  Excludes cancelled reservations, includes walk-ins (they consume
 *  kitchen capacity and show up in the day's lists). Backend caps the
 *  window at 31 days. */
export function useDaySummary(dateFrom: string | undefined, dateTo: string | undefined) {
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  const locationId = useAuthStore((s) => s.locationId);
  const enabled = isAuthenticated && !!locationId && !!dateFrom && !!dateTo;
  return useQuery({
    queryKey: ["day-summary", locationId, dateFrom, dateTo],
    queryFn: async () => {
      // ``enabled`` guards both values, but TypeScript can't see that.
      const params = new URLSearchParams({
        date_from: dateFrom ?? "",
        date_to: dateTo ?? "",
      });
      const res = await api
        .get(`reservations/day-summary/?${params}`)
        .json<PaginatedResponse<DaySummary>>();
      return res.items;
    },
    enabled,
    staleTime: 60_000,
  });
}
